import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

const inFlightDiscoverRequests = new Map();

function normalizeSearchContext(value = {}) {
  const centerLat = Number(value?.center?.lat);
  const centerLng = Number(value?.center?.lng);
  return {
    source: String(value?.source || ""),
    requestedDestination: String(value?.requestedDestination || ""),
    resolvedLabel: String(value?.resolvedLabel || ""),
    center:
      Number.isFinite(centerLat) && Number.isFinite(centerLng)
        ? { lat: centerLat, lng: centerLng }
        : null,
    confidence: String(value?.confidence || ""),
    candidates: (Array.isArray(value?.candidates) ? value.candidates : []).map((candidate) => {
      const lat = Number(candidate?.center?.lat);
      const lng = Number(candidate?.center?.lng);
      return {
        label: String(candidate?.label || ""),
        center: Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null,
        placeId: String(candidate?.placeId || ""),
        types: Array.isArray(candidate?.types) ? candidate.types : [],
      };
    }).filter((candidate) => candidate.label && candidate.center),
    requiresConfirmation: Boolean(value?.requiresConfirmation),
  };
}

function normalizeDiscoverResponse(data = {}) {
  const searchLat = Number(data?.searchCenter?.lat);
  const searchLng = Number(data?.searchCenter?.lng);
  return {
    answer: String(data.answer || ""),
    source: String(data.source || ""),
    capsules: Array.isArray(data.capsules) ? data.capsules : [],
    mapsSources: Array.isArray(data.mapsSources) ? data.mapsSources : [],
    searchCenter:
      Number.isFinite(searchLat) && Number.isFinite(searchLng)
        ? { lat: searchLat, lng: searchLng }
        : null,
    searchLocationLabel: String(data.searchLocationLabel || ""),
    fallbackCategories: Array.isArray(data.fallbackCategories) ? data.fallbackCategories : [],
    createdCapsuleIds: Array.isArray(data.createdCapsuleIds) ? data.createdCapsuleIds : [],
    reusedCapsuleIds: Array.isArray(data.reusedCapsuleIds) ? data.reusedCapsuleIds : [],
    errors: Array.isArray(data.errors) ? data.errors : [],
    searchContext: normalizeSearchContext(data?.searchContext || {}),
  };
}

function normalizeRequestCenter(center) {
  const lat = Number(center?.lat);
  const lng = Number(center?.lng);
  return {
    lat: Number.isFinite(lat) ? Number(lat.toFixed(6)) : null,
    lng: Number.isFinite(lng) ? Number(lng.toFixed(6)) : null,
  };
}

function buildInFlightRequestKey(payload) {
  const center = normalizeRequestCenter(payload.center);
  return JSON.stringify({
    prompt: String(payload.prompt || "").trim().replace(/\s+/g, " "),
    center,
    deviceId: String(payload.deviceId || "").trim(),
    maxResults: payload.maxResults || null,
    confirmedDestination: payload.confirmedDestination || null,
  });
}

export async function askGroundedDiscover({
  prompt,
  center,
  deviceId = "",
  maxResults = null,
  confirmedDestination = null,
}) {
  const callable = httpsCallable(functions, "discover_askGroundedPlaces");
  const payload = {
    prompt: String(prompt || "").trim(),
    center,
    deviceId: String(deviceId || "").trim(),
  };
  if (confirmedDestination?.center) {
    payload.confirmedDestination = {
      label: String(confirmedDestination?.label || confirmedDestination?.resolvedLabel || "").trim(),
      center: confirmedDestination.center,
      placeId: String(confirmedDestination?.placeId || "").trim(),
    };
  }
  const parsedMaxResults = Number(maxResults);
  if (Number.isFinite(parsedMaxResults) && parsedMaxResults > 0) {
    payload.maxResults = Math.trunc(parsedMaxResults);
  }
  const requestKey = buildInFlightRequestKey(payload);
  const existingRequest = inFlightDiscoverRequests.get(requestKey);
  if (existingRequest) return existingRequest;

  const request = callable(payload)
    .then((response) => normalizeDiscoverResponse(response?.data || {}))
    .finally(() => {
      inFlightDiscoverRequests.delete(requestKey);
    });
  inFlightDiscoverRequests.set(requestKey, request);
  return request;
}

export { normalizeDiscoverResponse };
